import { z } from 'zod';

/**
 * Backup domain.
 *
 * Modelled as three separate things on purpose:
 *  - a *storage target* is where bytes land (and has finite capacity worth
 *    monitoring in its own right),
 *  - a *plan* is the policy (what, when, how long to keep),
 *  - a *run* is one execution with its own progress, log and outcome.
 *
 * Conflating plan and run is the usual mistake, and it makes "did last night's
 * backup succeed" impossible to answer without parsing logs.
 */

export const STORAGE_TYPES = ['local', 'sftp', 's3', 'smb', 'nfs', 'ftp', 'webdav'] as const;
export type StorageType = (typeof STORAGE_TYPES)[number];

export const storageTargetSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  type: z.enum(STORAGE_TYPES),
  config: z.record(z.unknown()).default({}),
  customerId: z.string().nullable().default(null),
  capacityBytes: z.number().nullable().default(null),
  usedBytes: z.number().nullable().default(null),
  /** Soft cap; a run that would exceed it is refused before it starts. */
  quotaBytes: z.number().nullable().default(null),
  reachable: z.boolean().default(true),
  lastError: z.string().nullable().default(null),
  lastCheckedAt: z.number().nullable().default(null),
  enabled: z.boolean().default(true),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type StorageTarget = z.infer<typeof storageTargetSchema>;

export const BACKUP_SOURCE_TYPES = ['files', 'mysql', 'postgres', 'sqlite', 'vm', 'config', 'platform'] as const;
export type BackupSourceType = (typeof BACKUP_SOURCE_TYPES)[number];

export const BACKUP_MODES = ['full', 'incremental', 'differential'] as const;
export type BackupMode = (typeof BACKUP_MODES)[number];

/**
 * One thing a plan backs up. A plan may hold several (a database and the
 * folders beside it); they are built one after another and bundled into one
 * archive. `sourceType`/`sourceConfig` on the plan stay the first source, so
 * older builds and the automatic plans keep working unchanged.
 */
export const backupSourceSchema = z.object({
  type: z.enum(BACKUP_SOURCE_TYPES),
  /** e.g. { paths: ['/etc','/var/www'], exclude: ['*.log'] } or { databases: ['billing','cdr'], host: 'localhost' } */
  config: z.record(z.unknown()).default({}),
});
export type BackupSource = z.infer<typeof backupSourceSchema>;

export const backupPlanSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  enabled: z.boolean().default(true),
  resourceId: z.string().nullable().default(null),
  customerId: z.string().nullable().default(null),
  sourceType: z.enum(BACKUP_SOURCE_TYPES).default('files'),
  /** e.g. { paths: ['/etc','/var/www'], exclude: ['*.log'], database: 'billing' } */
  sourceConfig: z.record(z.unknown()).default({}),
  /** Every source of the plan, in order; empty on plans saved before multi-source plans existed. */
  sources: z.array(backupSourceSchema).default([]),
  targetId: z.string().nullable().default(null),
  /** Optional second target every successful archive is also copied to. */
  offsiteTargetId: z.string().nullable().default(null),
  /**
   * Set when the backups plugin created this plan on its own — `platform` for
   * the dashboard's own database, `resource` for a MagnusBilling box. It keeps
   * the plan from being recreated after an operator edits it, and marks it in
   * the UI; deleting an automatic plan turns that automation off for it.
   */
  autoSource: z.enum(['platform', 'resource']).nullable().default(null),
  scheduleCron: z.string().default('0 2 * * *'),
  mode: z.enum(BACKUP_MODES).default('full'),
  compression: z.enum(['none', 'gzip', 'zstd']).default('gzip'),
  encrypted: z.boolean().default(false),

  // Grandfather-father-son retention. Every tier is independent; an archive is
  // kept if *any* tier still wants it.
  keepLast: z.number().int().min(0).default(7),
  keepDaily: z.number().int().min(0).default(7),
  keepWeekly: z.number().int().min(0).default(4),
  keepMonthly: z.number().int().min(0).default(12),

  maxRuntimeMin: z.number().int().min(1).default(240),
  preScript: z.string().nullable().default(null),
  postScript: z.string().nullable().default(null),
  /** Checksum-verify the artifact after writing it. */
  verify: z.boolean().default(true),

  lastRunAt: z.number().nullable().default(null),
  lastStatus: z.string().nullable().default(null),
  nextRunAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type BackupPlan = z.infer<typeof backupPlanSchema>;

export const backupPlanCreateSchema = backupPlanSchema
  .omit({ id: true, createdAt: true, updatedAt: true, lastRunAt: true, lastStatus: true, nextRunAt: true })
  .partial()
  .required({ name: true });
export type BackupPlanCreate = z.infer<typeof backupPlanCreateSchema>;

export const BACKUP_RUN_STATES = ['queued', 'running', 'verifying', 'success', 'failed', 'cancelled'] as const;
export type BackupRunState = (typeof BACKUP_RUN_STATES)[number];

export const backupRunSchema = z.object({
  id: z.string(),
  planId: z.string(),
  state: z.enum(BACKUP_RUN_STATES),
  mode: z.enum(BACKUP_MODES),
  trigger: z.enum(['schedule', 'manual', 'retry']).default('schedule'),
  startedAt: z.number(),
  endedAt: z.number().nullable().default(null),
  bytesTotal: z.number().default(0),
  bytesWritten: z.number().default(0),
  filesTotal: z.number().default(0),
  artifactPath: z.string().nullable().default(null),
  offsitePath: z.string().nullable().default(null),
  offsiteError: z.string().nullable().default(null),
  checksum: z.string().nullable().default(null),
  verifiedAt: z.number().nullable().default(null),
  progressPct: z.number().min(0).max(100).default(0),
  error: z.string().nullable().default(null),
  log: z.string().nullable().default(null),
});
export type BackupRun = z.infer<typeof backupRunSchema>;

/**
 * Decide which runs to keep under grandfather-father-son.
 *
 * Returns the ids to delete. A run survives if it is claimed by any tier, so
 * tiers are additive rather than a precedence chain — the common bug is treating
 * them as exclusive and silently dropping the only monthly archive.
 */
export function applyRetention(
  runs: Array<{ id: string; startedAt: number; state: string }>,
  policy: Pick<BackupPlan, 'keepLast' | 'keepDaily' | 'keepWeekly' | 'keepMonthly'>,
): string[] {
  // Only successful runs are retention candidates; failures are pruned by age
  // elsewhere and must never count toward "we have 7 daily backups".
  const successes = runs
    .filter((r) => r.state === 'success')
    .sort((a, b) => b.startedAt - a.startedAt);

  const keep = new Set<string>();

  for (const run of successes.slice(0, policy.keepLast)) keep.add(run.id);

  const claimNewestPer = (bucketOf: (d: Date) => string, count: number) => {
    const seen = new Set<string>();
    for (const run of successes) {
      if (seen.size >= count) break;
      const bucket = bucketOf(new Date(run.startedAt));
      if (seen.has(bucket)) continue;
      seen.add(bucket);
      keep.add(run.id);
    }
  };

  claimNewestPer((d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`, policy.keepDaily);
  claimNewestPer((d) => `${d.getFullYear()}-W${isoWeek(d)}`, policy.keepWeekly);
  claimNewestPer((d) => `${d.getFullYear()}-${d.getMonth()}`, policy.keepMonthly);

  return successes.filter((r) => !keep.has(r.id)).map((r) => r.id);
}

function isoWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/** Recovery point objective: how much data a failure right now would lose. */
export function rpoSeconds(lastSuccessAt: number | null, nowTs = Date.now()): number | null {
  return lastSuccessAt === null ? null : Math.max(0, Math.floor((nowTs - lastSuccessAt) / 1000));
}

/**
 * Restoring a run: preview-then-apply, same split device provisioning uses.
 * `confirm` is required whenever the preview said so — it must equal the
 * exact string the preview returned (a database name, or the literal word
 * "RESTART" for the platform's own database), not just any truthy value.
 */
export const restoreRequestSchema = z.object({
  apply: z.boolean().default(false),
  confirm: z.string().optional(),
});
export type RestoreRequest = z.infer<typeof restoreRequestSchema>;

/** The plan's sources, for code that handles any plan: the list when it has one, else the single legacy source. */
export function planSources(plan: Pick<BackupPlan, 'sourceType' | 'sourceConfig' | 'sources'>): BackupSource[] {
  return plan.sources && plan.sources.length > 0 ? plan.sources : [{ type: plan.sourceType, config: plan.sourceConfig }];
}

/** The databases a database source covers: the list, else the single legacy name. */
export function sourceDatabases(config: Record<string, unknown>): string[] {
  const list = Array.isArray(config.databases) ? (config.databases as unknown[]).map(String).filter(Boolean) : [];
  if (list.length) return list;
  const one = typeof config.database === 'string' ? config.database.trim() : '';
  return one ? [one] : [];
}

/** Short human label of a source, for lists and badges. */
export function backupSourceLabel(source: BackupSource): string {
  switch (source.type) {
    case 'files': {
      const paths = Array.isArray(source.config.paths) ? (source.config.paths as string[]) : [];
      return paths.length ? `files: ${paths.slice(0, 2).join(', ')}${paths.length > 2 ? ` +${paths.length - 2}` : ''}` : 'files';
    }
    case 'mysql':
    case 'postgres': {
      const dbs = sourceDatabases(source.config);
      return `${source.type}: ${dbs.length ? dbs.slice(0, 3).join(', ') + (dbs.length > 3 ? ` +${dbs.length - 3}` : '') : '—'}`;
    }
    default:
      return source.type;
  }
}

/** POST /api/plugins/backups/browse/databases */
export interface BackupBrowseDatabasesRequest {
  type: 'mysql' | 'postgres';
  /** Browse on this resource over SSH; without it, from this server with the connection below. */
  resourceId?: string | null;
  host?: string;
  username?: string;
  password?: string;
}
export interface BackupBrowseDatabasesResponse {
  databases: string[];
  /** Where the list came from. */
  via: 'ssh' | 'local';
}

/** POST /api/plugins/backups/browse/folders */
export interface BackupBrowseFoldersRequest {
  resourceId?: string | null;
  /** Absolute directory to list; defaults to the root. */
  path?: string;
}
export interface BackupBrowseEntry {
  name: string;
  path: string;
  dir: boolean;
  /** Bytes, for files. */
  size?: number;
}
export interface BackupBrowseFoldersResponse {
  path: string;
  entries: BackupBrowseEntry[];
  via: 'ssh' | 'local';
}
